import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productData } from '../data/mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ExitIntentPopup } from '../components/ExitIntentPopup';
import { SizeChart } from '../components/SizeChart';
import { PayPalButton } from '../components/PayPalButton';
import { StripeButton } from '../components/StripeButton';
import { PaymentTrustBlock } from '../components/PaymentTrustBlock';
import { BundleUpsell } from '../components/BundleUpsell';
import { useSEO } from '../hooks/useSEO';
import { useJsonLd, breadcrumbSchema } from '../hooks/useJsonLd';
import axios from 'axios';
import { 
  Star, 
  Sparkles, 
  Heart, 
  Shield, 
  CheckCircle2,
  Truck,
  RotateCcw,
  ArrowRight,
  ShoppingBag,
  Clock,
  Gift
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const GoddessBodysuitLanding = () => {
  useSEO({
    title: 'Full Body Faja Colombiana — Snatched | CurveeForYou',
    description: 'Authentic Full Body Faja Colombiana with medical-grade compression, built-in bra, zipper crotch, and hook-and-eye closure. Included in the $89.99 Snatched Duo Bundle + free US shipping + 30-day guarantee.',
    canonical: 'https://curveeforyou.com/full-body-faja'
  });

  // Informational page — the Full Body Faja is only sold as part of the $89.99 Snatched Duo Bundle.
  // Product schema lives on the homepage (BundlePage) to avoid conflicting price data in Google Shopping.
  useJsonLd([
    breadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Full Body Faja', url: '/full-body-faja' }
    ])
  ]);

  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('Black');
  const [quantity, setQuantity] = useState(1);
  const [scrolled, setScrolled] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  // Get images for selected color
  const currentImages = productData.imagesByColor[selectedColor] || productData.imagesByColor.Black;

  // Hero video — autoplay loop (original Full Body Faja clip)
  const heroVideoUrl = "https://customer-assets.emergentagent.com/job_item-landing/artifacts/f7fd9ou4_Untitled%2086.mp4";
  const heroVideoPoster = currentImages[0];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBuyNow = () => {
    // Bundle-only store — every Buy Now redirects to the bundle homepage
    window.location.href = '/';
  };

  const savings = ((productData.originalPrice - productData.price) / productData.originalPrice * 100).toFixed(0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-rose-50/30 to-white pb-20 md:pb-0">
      {/* Exit Intent Popup */}
      <ExitIntentPopup />
      
      {/* Navigation Menu Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-md">
        <div className="container mx-auto px-6 max-w-7xl">
          {/* Top Bar */}
          <div className="flex items-center justify-between py-4">
            <Link to="/" aria-label="CurveeForYou home" data-testid="nav-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_item-landing/artifacts/feqbyn90_Logo%20curveeforyou.png"
                alt="CurveeForYou"
                className="h-20 md:h-24 w-auto"
              />
            </Link>
            
            {/* Desktop Menu — mirrors Bundle landing (lean, conversion-focused) */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8 ml-8 lg:ml-12">
              <a href="#reviews" className="text-gray-700 hover:text-[#7d1f3d] font-medium transition-colors">
                Reviews
              </a>
              <a href="#guarantee" className="text-gray-700 hover:text-[#7d1f3d] font-medium transition-colors">
                Guarantee
              </a>
              <Link to="/about" className="text-gray-700 hover:text-[#7d1f3d] font-medium transition-colors" data-testid="nav-about-link">
                About
              </Link>
              <Button 
                onClick={handleBuyNow}
                data-testid="nav-add-to-cart-button"
                className="bg-[#7d1f3d] hover:bg-[#5d1729] text-white px-6"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Buy Now
              </Button>
            </div>

            {/* Mobile Buy Now Button */}
            <Button 
              onClick={handleBuyNow}
              data-testid="mobile-nav-buy-now-button"
              className="md:hidden bg-[#7d1f3d] hover:bg-[#5d1729] text-white px-4 py-2 text-sm font-bold shadow-lg"
            >
              <ShoppingBag className="w-4 h-4 mr-1" />
              Buy Now
            </Button>
          </div>
        </div>
      </nav>

      {/* Limited Time Banner — matches Bundle landing */}
      <div className="bg-gradient-to-r from-[#d4af37] via-[#c49d2a] to-[#d4af37] text-[#5d1729] py-3 px-4 text-center font-medium mt-[112px] md:mt-[128px]">
        <div className="container mx-auto flex items-center justify-center gap-2 text-center">
          <Gift className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm md:text-lg font-bold uppercase tracking-wide">For A Limited Time — Snatched From Bust To Thigh: Buy 2 Fajas, Get FREE Boyshorts</span>
          <Gift className="w-5 h-5 flex-shrink-0" />
        </div>
      </div>

      {/* Hero Section */}
      <section className="container mx-auto px-6 max-w-7xl py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 lg:gap-12 items-start">
          {/* Product Image Gallery */}
          <div className="relative group lg:col-span-7">
            <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/20 to-transparent rounded-3xl blur-3xl group-hover:blur-2xl transition-all duration-500" />
            <div className="relative">
              {/* Bundle Deal Badge Above Image */}
              <div className="flex justify-center mb-4">
                <Badge className="bg-red-600 text-white font-bold text-base px-5 py-2 shadow-lg animate-pulse">
                  INCLUDED IN THE $89.99 BUNDLE 🔥
                </Badge>
              </div>
              
              {/* Main Image/Video */}
              <div className="overflow-hidden rounded-3xl shadow-2xl mb-4">
                {selectedImage === 0 ? (
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    poster={heroVideoPoster}
                    className="w-full h-auto object-cover"
                    data-testid="full-body-hero-video"
                    aria-label="Full Body Faja Colombiana product video"
                  >
                    <source src={heroVideoUrl} type="video/mp4" />
                    <img src={heroVideoPoster} alt="Full Body Faja Colombiana — medical-grade compression shapewear with built-in bra, zipper crotch and hook-and-eye front closure, sculpting the waist and smoothing the silhouette from bust to thigh" />
                  </video>
                ) : (
                  <img
                    src={currentImages[selectedImage]}
                    alt={`Full Body Faja Colombiana in ${selectedColor.toLowerCase()} — built-in bra, hook-and-eye front closure and bust-to-thigh compression`}
                    className="w-full h-auto object-cover object-top transform group-hover:scale-105 transition-transform duration-700"
                  />
                )}
              </div>

              {/* Thumbnail Gallery */}
              <div className="grid grid-cols-4 gap-2">
                {/* Video thumbnail */}
                <button
                  onClick={() => setSelectedImage(0)}
                  className={`relative overflow-hidden rounded-lg border-2 transition-all duration-300 ${
                    selectedImage === 0
                      ? 'border-[#7d1f3d] shadow-lg scale-105'
                      : 'border-gray-200 hover:border-[#d4af37] opacity-70 hover:opacity-100'
                  }`}
                >
                  <img
                    src={heroVideoPoster}
                    alt="Video thumbnail"
                    className="w-full h-full object-cover aspect-square"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="bg-white/90 rounded-full p-2">
                      <svg className="w-4 h-4 text-[#7d1f3d]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </button>

                {/* Show images starting from index 1 (index 0 is the video poster) */}
                {currentImages.slice(1).map((image, idx) => {
                  const imgIdx = idx + 1; // actual index in currentImages
                  return (
                    <button
                      key={image}
                      onClick={() => setSelectedImage(imgIdx)}
                      className={`relative overflow-hidden rounded-lg border-2 transition-all duration-300 ${
                        selectedImage === imgIdx
                          ? 'border-[#7d1f3d] shadow-lg scale-105'
                          : 'border-gray-200 hover:border-[#d4af37] opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${productData.name} - ${selectedColor} colorway - detail view ${imgIdx + 1}`}
                        className="w-full h-full object-cover object-top aspect-square"
                        loading="lazy"
                      />
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Size Chart - Below Gallery on Desktop */}
            <div className="hidden lg:block mt-6">
              <SizeChart />
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6 lg:col-span-3 lg:sticky lg:top-24">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold text-[#7d1f3d] mb-4 leading-tight">
                {productData.name}
              </h1>
              <div className="text-2xl text-[#d4af37] font-semibold italic">
                {productData.tagline}
              </div>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed whitespace-pre-line">
              {productData.description}
            </p>

            {/* Rating */}
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#d4af37] text-[#d4af37]" />
                ))}
              </div>
              <span className="text-gray-600 font-medium">4.9/5 (1,247 reviews)</span>
            </div>

            {/* Bundle-only CTA — replaces price, size/color selectors, individual checkout */}
            <div id="payment-section" className="mb-6">
              <Link
                to="/"
                data-testid="fbf-checkout-bundle-cta"
                className="block bg-gradient-to-br from-[#7d1f3d] to-[#5d1729] text-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-shadow"
              >
                <p className="text-[#d4af37] text-xs font-bold uppercase tracking-wider mb-2">Bundle Exclusive</p>
                <p className="text-white font-bold text-xl mb-1">Only available in the Snatched Duo Bundle</p>
                <p className="text-white/80 text-sm mb-4">Get this Full Body Faja together with the Open-Bust Faja + FREE Seamless Boyshorts.</p>
                <div className="bg-[#d4af37] text-[#5d1729] text-center font-bold py-3 rounded-lg">
                  Get the Bundle — $89.99 →
                </div>
              </Link>
            </div>

            <div className="mb-6">
              {/* Size chart mobile helper */}
              <div className="lg:hidden">
                <SizeChart />
              </div>
            </div>

            {/* Legacy CTA block hidden — moved to bundle */}
            <div className="hidden">
              <BundleUpsell hookText="" />

              <StripeButton
                selectedSize={selectedSize}
                selectedColor={selectedColor}
                quantity={quantity}
                productData={productData}
              />

              <PaymentTrustBlock />

              <PayPalButton
                selectedSize={selectedSize}
                selectedColor={selectedColor}
                quantity={quantity}
                productData={productData}
              />
            </div>

            <div className="space-y-3 pt-4">
              {/* Trust Badges — GUARANTEE PROMINENT */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-500 rounded-xl p-6 mt-4">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Shield className="w-8 h-8 text-green-600" />
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-700">30-Day Money-Back Guarantee</p>
                    <p className="text-sm text-green-600">Love it or your money back - no questions asked</p>
                  </div>
                  <Shield className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-center text-sm text-gray-700">
                  Try it risk-free! If you&apos;re not 100% satisfied, return it within 30 days for a full refund.
                </p>
              </div>
              
              <div className="flex items-center justify-center gap-6 text-sm text-gray-600 mt-4">
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#d4af37]" />
                  <span>Free Shipping</span>
                </div>
                <div className="flex items-center gap-2">
                  <RotateCcw className="w-5 h-5 text-[#d4af37]" />
                  <span>Easy Returns</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-700">Risk-Free</span>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="flex gap-4 pt-4 border-t border-gray-200">
              {productData.benefits.slice(0, 3).map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle2 className="w-4 h-4 text-[#d4af37] flex-shrink-0" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-gradient-to-b from-white to-rose-50/50 py-20">
        <div className="container mx-auto px-6 max-w-7xl">

          {/* What Makes Fajas Different */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-gradient-to-br from-[#7d1f3d] to-[#5d1729] text-white rounded-2xl shadow-2xl p-8 md:p-12 text-center">
              <h3 className="text-3xl lg:text-4xl font-bold mb-6 text-[#d4af37]">
                What Makes Fajas Different
              </h3>
              <p className="text-xl md:text-2xl font-semibold mb-4">
                Shapewear smooths. <span className="text-[#d4af37]">Fajas sculpt.</span>
              </p>
              <p className="text-lg text-rose-100 mb-6 max-w-2xl mx-auto">
                High compression delivers instant waist cinching.
              </p>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 max-w-2xl mx-auto">
                <p className="text-base md:text-lg leading-relaxed">
                  TikTok&apos;s bestselling faja sold <strong className="text-[#d4af37]">$2.3M in 3 months at $46</strong>. Get the Snatched Duo Bundle — 2 fajas + free boyshorts — for <strong className="text-[#d4af37]">$89.99</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* Key Features */}
          <div className="text-center mb-12">
            <h3 className="text-4xl lg:text-5xl font-bold text-[#7d1f3d] mb-4">
              Key Features
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">
              Authentic Colombian construction. Real sculpting power.
            </p>
          </div>

          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">🚻</span>
                  <p className="text-gray-700"><strong>Zipper crotch</strong> — Bathroom access in seconds, no undressing</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">⚡</span>
                  <p className="text-gray-700"><strong>High compression</strong> — Instant waist cinching, medical-grade quality</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">🔒</span>
                  <p className="text-gray-700"><strong>Hook-and-eye closure</strong> — 3 rows, adjustable fit YOU control</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">🍑</span>
                  <p className="text-gray-700"><strong>Butt lifter panels</strong> — Enhance curves, don&apos;t flatten</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">💃</span>
                  <p className="text-gray-700"><strong>Built-in bra</strong> — No separate undergarments needed</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">📏</span>
                  <p className="text-gray-700"><strong>Mid-thigh coverage</strong> — Prevents roll-up and chafing</p>
                </div>
                <div className="flex items-start gap-3 md:col-span-2 md:justify-self-center">
                  <span className="text-2xl flex-shrink-0">👗</span>
                  <p className="text-gray-700"><strong>Seamless design</strong> — Invisible under clothes</p>
                </div>
              </div>
            </div>
          </div>

          {/* Solution */}
          <div className="text-center mb-12">
            <h3 className="text-4xl lg:text-5xl font-bold text-[#7d1f3d] mb-6">
              ✨ This Faja Delivers All of This
            </h3>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              Slip into your tightest dress and see your <strong>snatched waist</strong> and <strong>lifted curves</strong> in the mirror. Move freely. Breathe easily. Walk taller. <strong>Own the room.</strong>
            </p>
            <div className="inline-block bg-gradient-to-r from-[#7d1f3d] to-[#9d2f4d] text-white px-8 py-4 rounded-full text-lg font-semibold">
              From breakfast meetings to midnight dancing — stay snatched all day long
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="text-center mb-16 mt-20">
            <h3 className="text-3xl lg:text-4xl font-bold text-[#7d1f3d] mb-4">
              Built to Sculpt
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Authentic Colombian construction. Real medical-grade compression.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {productData.features.map((feature, idx) => {
              const IconComponent = {
                Sparkles,
                Heart,
                Shield,
                Star
              }[feature.icon];

              return (
                <Card key={idx} className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#7d1f3d] to-[#9d2f4d] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-[#7d1f3d] mb-3">
                      {feature.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="reviews" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h3 className="text-4xl lg:text-5xl font-bold text-[#7d1f3d] mb-4">
              Loved by Thousands
            </h3>
            <p className="text-lg text-gray-600">
              Real women, real results, real confidence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {productData.testimonials.map((testimonial, idx) => (
              <Card key={idx} className="border-2 border-[#d4af37]/30 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#d4af37] text-[#d4af37]" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic leading-relaxed">
                    &quot;{testimonial.text}&quot;
                  </p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-[#7d1f3d]">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.location}</p>
                    </div>
                    {testimonial.verified && (
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        Verified
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* GUARANTEE SECTION - BUILD TRUST */}
      <section id="guarantee" className="py-20 bg-gradient-to-b from-green-50 to-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border-4 border-green-500">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                  <Shield className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-4xl font-bold text-gray-900 mb-4">
                  Our Iron-Clad 30-Day Money-Back Guarantee
                </h3>
                <p className="text-xl text-gray-700 leading-relaxed">
                  We&apos;re so confident you&apos;ll LOVE your faja that we&apos;re removing ALL the risk.
                </p>
              </div>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-bold text-lg text-gray-900">Try it for 30 full days</p>
                    <p className="text-gray-600">Wear it. Wash it. Love it. If you don&apos;t, we don&apos;t want your money.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-bold text-lg text-gray-900">No questions asked</p>
                    <p className="text-gray-600">Don&apos;t like the fit? Changed your mind? No problem. Full refund, no hassle.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-bold text-lg text-gray-900">Easy returns</p>
                    <p className="text-gray-600">Simple return process. We&apos;ll even send you a prepaid return label.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-bold text-lg text-gray-900">100% of your money back</p>
                    <p className="text-gray-600">Every. Single. Penny. No restocking fees. No &quot;store credit only.&quot;</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
                <p className="text-center text-lg font-semibold text-gray-900">
                  <strong className="text-green-700">Bottom line:</strong> You have ZERO risk. The only thing you risk is missing out on feeling confident and comfortable in your own skin.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Semantic Content Section 1: About Shapewear */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#7d1f3d] mb-6">
            Shapewear for Smoothing, Support, and Everyday Confidence
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            CurveeForYou offers authentic Fajas Colombianas designed to deliver medical-grade compression, instant waist cinching, and a snatched silhouette under any outfit. Our Full Body Faja Colombiana is built for women who want real sculpting power and all-day wearability.
          </p>
        </div>
      </section>

      {/* Semantic Content Section 2: Fit Guidance */}
      <section className="py-16 bg-gradient-to-b from-rose-50/30 to-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#7d1f3d] mb-6">
            How to Choose the Right Fit
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            For the best result, compare your bust, waist, and hip measurements to the size guide before ordering. If you are between sizes or prefer a more relaxed feel for everyday wear, sizing up may be the better option. CurveeForYou combines shaping support with wearable comfort, so fit guidance should be easy to find on the page.
          </p>
        </div>
      </section>

      {/* Cross-sell — Open-Bust Faja + Bundle */}
      <section id="other-products" className="py-20 bg-gradient-to-b from-rose-50/30 to-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-12">
            <span className="inline-block bg-rose-100 text-[#7d1f3d] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              Also from CurveeForYou
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#7d1f3d] mb-4">
              The Right Faja for Every Outfit
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Add the perfect companion to your shapewear collection — or grab both and save.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Open-Bust Faja card */}
            <Link
              to="/open-bust-faja"
              data-testid="home-open-bust-card"
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 border border-rose-100"
            >
              <div className="aspect-[4/3] bg-rose-50 overflow-hidden">
                <img
                  src="https://curvluxe.com/cdn/shop/files/fajas-colombianas-tummy-control-shapewear-bodysuit-for-wedding-or-prom-9381302.jpg"
                  alt="Open-Bust Tummy Control Faja"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <span className="inline-block bg-[#d4af37]/20 text-[#7d1f3d] px-3 py-1 rounded-full text-xs font-bold uppercase mb-3">
                  Wedding · Prom · Strapless
                </span>
                <h3 className="text-2xl font-bold text-[#7d1f3d] mb-2 group-hover:text-[#5d1729] transition-colors">
                  Open-Bust Tummy Control Faja
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Bring your own bra. Same snatched silhouette. Perfect for strapless dresses, prom, or any specific bra you love.
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-[#7d1f3d]">$39.99</span>
                    <span className="text-sm text-gray-400 line-through">$59.99</span>
                  </div>
                  <span className="text-[#7d1f3d] font-semibold group-hover:translate-x-1 transition-transform">
                    Shop →
                  </span>
                </div>
              </div>
            </Link>

            {/* Bundle card */}
            <Link
              to="/bundle"
              data-testid="home-bundle-card"
              className="group bg-gradient-to-br from-[#7d1f3d] to-[#5d1729] text-white rounded-2xl shadow-xl hover:shadow-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 relative"
            >
              <div className="absolute top-4 right-4 bg-[#d4af37] text-[#5d1729] px-4 py-1.5 rounded-full text-xs font-bold uppercase shadow-lg z-10">
                FREE Gift
              </div>
              <div className="aspect-[4/3] grid grid-cols-2 gap-1 bg-rose-50">
                <img
                  src="https://customer-assets.emergentagent.com/job_item-landing/artifacts/ymvx3tpt_openart-14c4bd9b-a791-48cc-996b-d64c115a23b4.png"
                  alt="Full Body Faja Colombiana"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <img
                  src="https://curvluxe.com/cdn/shop/files/fajas-colombianas-tummy-control-shapewear-bodysuit-for-wedding-or-prom-9381302.jpg"
                  alt="Open-Bust Tummy Control Faja"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <span className="inline-block bg-[#d4af37]/30 text-[#d4af37] px-3 py-1 rounded-full text-xs font-bold uppercase mb-3">
                  The Snatched Duo
                </span>
                <h3 className="text-2xl font-bold mb-2 text-[#d4af37]">
                  Get Both + FREE Boyshorts
                </h3>
                <p className="text-rose-100 text-sm mb-4">
                  Snatched from bust to thigh — buy any 2 fajas and we&apos;ll throw in our high-quality seamless boyshorts, free.
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-[#d4af37]">$89.99</span>
                    <span className="text-sm text-rose-200">+ FREE Boyshorts</span>
                  </div>
                  <span className="text-[#d4af37] font-semibold group-hover:translate-x-1 transition-transform">
                    Get the Bundle →
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* New to Shapewear Guide Section */}
      <section id="shapewear-guide" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#7d1f3d] mb-4">
              New to Shapewear?
            </h2>
            <p className="text-xl text-gray-600">
              Not sure where to start? We&apos;ve got you.
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center italic">
              Shapewear should feel like a secret weapon, not a mystery. Here&apos;s everything you need to know before you buy.
            </p>

            <div className="space-y-8">
              <div className="bg-gradient-to-br from-rose-50 to-white border-l-4 border-[#7d1f3d] p-6 rounded-r-xl">
                <h3 className="text-xl font-bold text-[#7d1f3d] mb-3">What does shapewear actually do?</h3>
                <p className="text-gray-700 leading-relaxed">
                  It smooths, supports, and shapes your silhouette under clothing — without changing who you are. Think of it as the foundation that makes every outfit work harder for you.
                </p>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-white border-l-4 border-[#7d1f3d] p-6 rounded-r-xl">
                <h3 className="text-xl font-bold text-[#7d1f3d] mb-3">How tight should it feel?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Supportive, not suffocating. Good shapewear holds you in without making you feel held back. You should be able to breathe, sit, and move through your day comfortably.
                </p>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-white border-l-4 border-[#7d1f3d] p-6 rounded-r-xl">
                <h3 className="text-xl font-bold text-[#7d1f3d] mb-3">Should I size up?</h3>
                <p className="text-gray-700 leading-relaxed">
                  If you want an easy, comfortable everyday fit — yes. If you want a firmer sculpting effect, go by your measurements. When in doubt, check our size guide before ordering.
                </p>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-white border-l-4 border-[#7d1f3d] p-6 rounded-r-xl">
                <h3 className="text-xl font-bold text-[#7d1f3d] mb-3">What&apos;s the difference between smoothing and firm control?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Smoothing styles are lighter — great for daily wear under anything. Firm control is for when you want a more sculpted look, like under a fitted dress or for a special occasion.
                </p>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-white border-l-4 border-[#7d1f3d] p-6 rounded-r-xl">
                <h3 className="text-xl font-bold text-[#7d1f3d] mb-3">Can I wear shapewear every day?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Absolutely. Our lighter styles are designed exactly for that — breathable, comfortable, and seamless enough to forget you&apos;re wearing them.
                </p>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-white border-l-4 border-[#7d1f3d] p-6 rounded-r-xl">
                <h3 className="text-xl font-bold text-[#7d1f3d] mb-3">What can I wear it under?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Dresses, skirts, jumpsuits, workwear, fitted basics — anything. The right style depends on your outfit and what you want to smooth or support.
                </p>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-white border-l-4 border-[#7d1f3d] p-6 rounded-r-xl">
                <h3 className="text-xl font-bold text-[#7d1f3d] mb-3">How do I choose the right style?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Start with your goal. Tummy smoothing? Go for a brief or high-waist style. All-over shaping? A bodysuit. Light everyday coverage? Something soft and seamless. Still unsure — we&apos;re here to help.
                </p>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-white border-l-4 border-[#7d1f3d] p-6 rounded-r-xl">
                <h3 className="text-xl font-bold text-[#7d1f3d] mb-3">How do I care for it?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Gentle wash, careful drying. Following the care label on each piece keeps the compression, stretch, and fabric quality lasting longer.
                </p>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-white border-l-4 border-[#7d1f3d] p-6 rounded-r-xl">
                <h3 className="text-xl font-bold text-[#7d1f3d] mb-3">I&apos;ve never worn shapewear before. Where do I start?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Start light. A soft smoothing brief or seamless boyshort is the easiest introduction. Once you know what works for your body, you can explore firmer styles from there.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#7d1f3d] to-[#9d2f4d] text-white p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-3">Need help finding the right fit?</h3>
                <p className="leading-relaxed mb-4">
                  Every CurveeForYou product includes sizing guidance and full product details. Still have questions? Email us at <a href="mailto:info@curveeforyou.com" className="underline font-semibold hover:text-[#d4af37]">info@curveeforyou.com</a> — we&apos;ll help you find exactly what you need.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gradient-to-b from-rose-50/50 to-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-16">
            <h3 className="text-4xl lg:text-5xl font-bold text-[#7d1f3d] mb-4">
              Common Questions
            </h3>
            <p className="text-lg text-gray-600">
              Everything you need to know
            </p>
          </div>

          <div className="space-y-6">
            {productData.faqs.map((faq, idx) => (
              <div 
                key={idx} 
                className="bg-white border-2 border-gray-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-all"
              >
                <h4 className="text-left font-semibold text-[#7d1f3d] text-lg mb-3">
                  {faq.question}
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#7d1f3d] via-[#9d2f4d] to-[#7d1f3d] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30" />
        
        <div className="container mx-auto px-6 max-w-7xl text-center relative z-10">
          <h3 className="text-4xl lg:text-5xl font-bold mb-6">
            Ready to Get Snatched?
          </h3>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Join thousands of women who&apos;ve discovered their new favorite bodysuit
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Button 
              onClick={handleBuyNow}
              className="bg-white text-[#7d1f3d] hover:bg-[#d4af37] hover:text-white text-lg px-12 py-7 rounded-xl shadow-2xl transform hover:scale-105 transition-all duration-300 font-bold"
            >
              Get the Bundle — $89.99
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>30-Day Money-Back Guarantee</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>Free US Shipping</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>Secure Checkout</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2a1010] text-white py-12">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="text-xl font-bold text-[#d4af37] mb-4">CurveeForYou</h4>
              <p className="text-gray-400">
                You&apos;re beautiful. Now the world knows it.
              </p>
            </div>
            <div>
              <h5 className="font-semibold mb-4 text-[#d4af37]">Quick Links</h5>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog & Guides</Link></li>
                <li><Link to="/shipping" className="hover:text-white transition-colors">Shipping Info</Link></li>
                <li><Link to="/returns" className="hover:text-white transition-colors">Returns</Link></li>
                <li><Link to="/size-guide" className="hover:text-white transition-colors">Size Guide</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4 text-[#d4af37]">Customer Service</h5>
              <ul className="space-y-2 text-gray-400">
                <li>Email: info@curveeforyou.com</li>
                <li>Mon-Fri: 9AM-6PM EST</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-gray-400 text-sm">
            <div className="text-sm opacity-90 mb-3 leading-relaxed">
              <p className="font-semibold mb-1">CurveeForYou</p>
              <p>1209 Mountain Road Place Northeast, Suite N</p>
              <p>Albuquerque, NM 87110, United States</p>
              <p className="mt-2">
                <a href="tel:+18002985173" className="hover:text-[#d4af37] transition-colors">Tel: 800 298 5173</a>
                {' · '}
                <a href="mailto:info@curveeforyou.com" className="hover:text-[#d4af37] transition-colors">info@curveeforyou.com</a>
              </p>
            </div>
            <p>&copy; 2025 CurveeForYou. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* FAQPage Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": productData.faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })}
      </script>

      {/* Sticky Mobile Buy Now Bar - always visible on mobile */}
      <div
        data-testid="mobile-sticky-cta"
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
      >
        <div className="flex items-center gap-3">
          <div className="flex flex-col leading-tight">
            <span className="text-xs text-gray-500">Bundle Exclusive</span>
            <span className="text-lg font-bold text-[#7d1f3d]">
              $89.99
            </span>
          </div>
          <Button
            onClick={handleBuyNow}
            data-testid="mobile-sticky-buy-now-button"
            className="flex-1 bg-[#7d1f3d] hover:bg-[#5d1729] text-white font-bold text-base py-6 shadow-lg animate-pulse-slow"
          >
            <ShoppingBag className="w-5 h-5 mr-2" />
            GET THE BUNDLE
          </Button>
        </div>
      </div>
    </div>
  );
};
